import { Badge } from './Chip';

import { useT, tNow } from '@/i18n/languageStore';

/** specs/003 #8: concrete facts about a member, as the server sends them with every public view. */
export interface TrustSummaryFacts {
  idVerified: boolean;
  helped: number;
  kudos: number;
  /** How many neighbors vouched for them; absent from older servers. */
  vouches?: number;
  since: string | null;
  /** Their phone number was confirmed with a one-time code; absent when it was not. */
  phoneVerified?: boolean;
}

export interface TrustSummarySource {
  trustSummary?: TrustSummaryFacts | null;
  /** Older servers send only the score. */
  trustScore?: number | null;
}

/** "ID verified · Helped 12 · 9 kudos", "New neighbor" when there is nothing yet, or null with no data. */
export function trustSummaryText(member: TrustSummarySource): string | null {
  const facts = member.trustSummary;
  if (!facts)
    return member.trustScore != null
      ? tNow('{trustScore}% trust', { trustScore: member.trustScore })
      : null;
  const parts = [
    facts.idVerified ? tNow("ID verified") : null,
    facts.phoneVerified ? tNow("Phone verified") : null,
    facts.helped > 0 ? tNow('Helped {helped}', { helped: facts.helped }) : null,
    facts.kudos > 0 ? `${facts.kudos} kudos` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : tNow('New neighbor');
}

/** The trust summary as a badge, in place of a single opaque percentage. */
export function TrustSummary({
  member,
  small = false,
}: {
  member: TrustSummarySource;
  small?: boolean;
}) {
  const t = useT();
  const text = trustSummaryText(member);
  return text ? <Badge small={small}>{t(text)}</Badge> : null;
}
